import Head from 'next/head';
import Script from 'next/script';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTheme } from '@forma/forma-ui-theme';
import MainBanner2 from '@/components/blocks/MainBanner2';
import ApplicationsList2 from '@/components/blocks/ApplicationsList2';
import MetaTags from '@/components/MetaTags';
import useMetaTags from '@/hooks/useMetaTags';
import getHeadersFromServer from '@/helpers/getHeadersFromServer';
import SocialMediaSlider from '@/components/blocksMiniBuisness/SocialMediaSlider';
import Counts2 from '@/components/blocks/Counts2';
import AdvantagesBanner3 from '@/components/blocks/AdvantagesBanner3';
import RequestDemoBanner from '@/components/blocks/RequestDemoBanner';
import Cards from '@/components/blocks/Cards';
import WidgetsBanner from '@/components/blocks/WidgetsBanner';

import mockApi from '@/api/mockApi';

import styles from './Homepage.module.css';

const enableAnalytics = process.env.ENABLE_ANALYTICS === 'true';
const dmpCounterId = process.env.DMP_COUNTER_ID;

export const getServerSideProps = async ({ req, res, params }) => {
  try {
    res.setHeader(
      'Cache-Control',
      'public, s-maxage=14400, stale-while-revalidate=10'
    );
    const mainData = await mockApi.getMainData(params.locale);
    const homeData = await mockApi.getPageData(params.locale, 'home');

    const headersParse = await getHeadersFromServer(req);
    return {
      props: {
        initialState: {
          main: mainData,
        },
        home: homeData,
        headersParse,
        ...(await serverSideTranslations(params.locale, ['common', 'uikit'])),
      },
    };
  } catch {
    return {
      notFound: true
    };
  }
};

const Home = ({ home }) => {
  const { t } = useTranslation();

  const metaData = {
    title: t('main_title'),
    description: t('main_description'),
    keywords: t('main_keywords'),
  };
  const { lang, viewport } = useTheme();
  useMetaTags({ lang, ...metaData });

  if (!home) return null;

  const isPhone = ['phone', 'tabletS'].includes(viewport);
  const { main_banner, advantages, counts, applications, media, automate, request_demo } = home;

  return (
    <div>
      <Head>
        <title>{metaData.title}</title>
        <MetaTags {...metaData} />
      </Head>

      {enableAnalytics && (
        <Script
          strategy="afterInteractive"
          id="p_dmp_load"
          src={`https://p.dmp.one/sync?stock_key=${dmpCounterId}`}
          referrerpolicy="no-referrer-when-downgrade"
          charset="UTF-8"
          async
        />
      )}

      <MainBanner2 {...main_banner} variant="white" />
      {!isPhone && <Counts2 {...counts} variant="white" />}
      {!isPhone && <SocialMediaSlider {...media} className={styles.media} marqueeClassName={styles.marquee} variant="white" />}
      <Cards {...automate} />
      <AdvantagesBanner3 {...advantages} variant="white" />
      <ApplicationsList2 items={applications} />
      <WidgetsBanner />
      <RequestDemoBanner variant="white" {...request_demo} />
    </div>
  );
};

export default Home;
